import React, { useState } from 'react'

import { SxProps } from '@mui/material'

import type {
  TGeneralConfigDto,
  TMultiOptionsConfigDto,
  TSingleConfigDto,
  TSingleConfigType,
  TUpdateConfigsRequestBody
} from '@homepal/api-contract'

import { PeriodType } from 'types/GlobalCustomer'
import { Category } from 'types/GlobalDashboardGroup'

import { useConfigActions } from 'redux/actions/Configs'
import { useConfigsData } from 'redux/hooks/Configs'
import { useCustomer } from 'redux/hooks/Customer'

import { GeneralConfigAttribute } from 'types/GlobalConfig'

import { sortAlphabeticalAsc } from 'utils/sortHelper'

import Box from 'components_new/atoms/Box'
import Tag from 'components_new/atoms/Tag'
import Tooltip from 'components_new/atoms/Tooltip'

import { Option } from 'components_new/molecules/SelectSearch'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import { TInputType } from 'components_new/molecules/SettingsEditDialog/utils'

import ObjectRowTypesDialog from 'components_new/organisms/dialogs/ObjectRowTypesDialog'

import {
  getExcludedSummary,
  getGeneralOptions,
  getNewSummary,
  isPendingDataRun
} from './utils'
import { getParsedDate } from 'utils/dateParser'
import { useTranslation } from 'language/translator'

interface ConfigsProps {
  sx?: SxProps
}

export interface TargetSection {
  category: Category
  generals: { [attribute: string]: TGeneralConfigDto }
}

const Configs = (props: ConfigsProps) => {
  const { sx } = props

  const configsData = useConfigsData()
  const customer = useCustomer()

  const { tryUpdateConfig } = useConfigActions()
  const { t, translateEnum, lang } = useTranslation()

  const sections = Object.values(configsData)

  const maxDate = new Date()

  maxDate.setMonth(maxDate.getMonth() - 1)

  /*-- dialogs --*/
  const [dialog, setDialog] = useState<{
    open: boolean
    title: string
    type: TInputType
    configType: TSingleConfigType
    data: {
      attribute: string
      category: Category
      options: Option[]
      unit: string | null
      value: string
    }
  }>({
    open: false,
    title: '',
    type: 'number',
    configType: 'NUMBER',
    data: {
      attribute: '',
      category: Category.ECONOMY,
      options: [],
      unit: null,
      value: ''
    }
  })

  // the MULTI_OPTIONS config being edited and its category
  const [multiOptions, setMultiOptions] = useState<{
    open: boolean
    general: TMultiOptionsConfigDto | null
    category: Category
  }>({ open: false, general: null, category: Category.RENTAL })

  const handleCloseDialog = () => {
    setDialog((prevState) => ({ ...prevState, open: false }))
  }

  const handleOpenDialog = (general: TSingleConfigDto, category: Category) => {
    let inputType: TInputType = 'number'
    let inputOptions: Option[] = []

    if (general.type === 'OPTIONS') {
      inputType = 'select'
      inputOptions = getGeneralOptions(general, t)
    }

    if (general.type === 'MONTH') {
      inputType = 'year_month'
    }

    setDialog({
      open: true,
      title: translateEnum(
        'general_config_attributes',
        general.attribute as GeneralConfigAttribute
      ).title,
      type: inputType,
      configType: general.type,
      data: {
        attribute: general.attribute,
        category: category,
        options: inputOptions,
        unit: general.unit,
        value: general.value ? general.value.toString() : ''
      }
    })
  }

  const handleEditDialogValue = (value: string) => {
    setDialog((prevState) => ({
      ...prevState,
      data: { ...prevState.data, value }
    }))
  }

  const handleSubmitDialog = () => {
    let body: TUpdateConfigsRequestBody<PeriodType> = {}

    if (dialog.configType === 'OPTIONS') {
      body = {
        [dialog.data.attribute]:
          dialog.data.value === 'none' ? null : dialog.data.value
      }
    } else if (dialog.configType === 'MONTH') {
      body = {
        [dialog.data.attribute]: dialog.data.value
      }
    } else {
      body = {
        [dialog.data.attribute]: dialog.data.value
          ? parseInt(dialog.data.value)
          : null
      }
    }

    tryUpdateConfig(body, dialog.data.category)
  }

  return (
    <Box sx={{ maxWidth: 'content', ...sx }}>
      {sections
        .sort((a, b) =>
          sortAlphabeticalAsc(
            translateEnum('categories', a.category),
            translateEnum('categories', b.category)
          )
        )
        .map((section, i) => {
          const generals = Object.values(section.generals)

          return (
            <Box key={i}>
              {generals.length > 0 ? (
                <SettingsGroup
                  title={translateEnum('categories', section.category)}
                >
                  {generals.map((general) => {
                    // multiple selection
                    if (general.type === 'OPTIONS') {
                      return (
                        <SettingsItem
                          key={general.attribute}
                          onClick={() =>
                            handleOpenDialog(general, section.category)
                          }
                          title={
                            translateEnum(
                              'general_config_attributes',
                              general.attribute as GeneralConfigAttribute
                            ).title
                          }
                          value={
                            general.options.find(
                              (option) => option.value === general.value
                            )?.label ?? null
                          }
                          variant="edit"
                        />
                      )
                    } else if (general.type === 'MULTI_OPTIONS') {
                      const newSummary = getNewSummary(general, t)

                      return (
                        <SettingsItem
                          key={general.attribute}
                          onClick={() =>
                            setMultiOptions({
                              open: true,
                              general,
                              category: section.category
                            })
                          }
                          title={
                            translateEnum(
                              'general_config_attributes',
                              general.attribute as GeneralConfigAttribute
                            ).title
                          }
                          value={getExcludedSummary(general, t)}
                          valueAdornments={
                            <>
                              {newSummary ? (
                                <Tag color="success" label={newSummary} />
                              ) : null}
                              {isPendingDataRun(
                                general,
                                customer?.latest_job_run
                              ) ? (
                                    <Tooltip
                                      title={t(
                                        'configuration',
                                        'pending_data_run_tooltip',
                                        false,
                                        {
                                          date: getParsedDate(
                                            general.changed_at,
                                            lang,
                                            { time: 'full' }
                                          )
                                        }
                                      )}
                                    >
                                      <Tag
                                        color="info"
                                        label={t(
                                          'configuration',
                                          'pending_data_run'
                                        )}
                                        startIconName="UpdateOutlined"
                                      />
                                    </Tooltip>
                                  ) : null}
                            </>
                          }
                          variant="edit"
                        />
                      )
                    } else if (general.type === 'MONTH') {
                      return (
                        <SettingsItem
                          key={general.attribute}
                          onClick={() =>
                            handleOpenDialog(general, section.category)
                          }
                          title={
                            translateEnum(
                              'general_config_attributes',
                              general.attribute as GeneralConfigAttribute
                            ).title
                          }
                          value={getParsedDate(general.value as string, lang)}
                          variant="edit"
                        />
                      )
                    }

                    // number input
                    return (
                      <SettingsItem
                        key={general.attribute}
                        onClick={() =>
                          handleOpenDialog(general, section.category)
                        }
                        title={
                          translateEnum(
                            'general_config_attributes',
                            general.attribute as GeneralConfigAttribute
                          ).title
                        }
                        value={`${general.value} ${general.unit || ''}`}
                        variant="edit"
                      />
                    )
                  })}
                </SettingsGroup>
              ) : null}
            </Box>
          )
        })}

      {/*-- dialogs --*/}
      <SettingsEditDialog
        maxDate={maxDate.toISOString()}
        onChange={handleEditDialogValue}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitDialog}
        open={dialog.open}
        options={dialog.data.options}
        title={dialog.title}
        type={dialog.type}
        unit={dialog.data.unit}
        value={dialog.data.value}
      />
      <ObjectRowTypesDialog
        general={multiOptions.general}
        onClose={() =>
          setMultiOptions((prevState) => ({ ...prevState, open: false }))
        }
        onSubmit={(objectRowTypes) => {
          if (multiOptions.general) {
            tryUpdateConfig(
              { [multiOptions.general.attribute]: objectRowTypes },
              multiOptions.category
            )
          }
        }}
        open={multiOptions.open}
      />
    </Box>
  )
}

export default Configs
